import { useQuery } from '@tanstack/react-query';
import { UseQueryOptions } from '@tanstack/react-query';
import { MedicalRecordService } from '../medical-record.service';
import {
	resolveMedicalRecordSessionPollInterval,
	resolveMedicalRecordSessionUntilSummaryPollInterval,
} from '@/utils/medicalRecordSessionPostProcessing';

type MedicalRecordSessionPollMode = 'post-process' | 'until-summary';

type UseGetMedicalRecordSessionOptions = Omit<
	UseQueryOptions<MedicalRecords.MedicalRecordSession, Error>,
	'queryKey' | 'queryFn'
> & {
	pollMode?: MedicalRecordSessionPollMode;
};

export const useGetMedicalRecordByPatient = (
	patientId: string,
	options?: Omit<
		UseQueryOptions<MedicalRecords.MedicalRecord, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	return useQuery({
		queryKey: ['medical-record', patientId],
		queryFn: () => {
			return MedicalRecordService.getByPatient(patientId);
		},
		enabled: !!patientId,
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetMedicalRecordSessions = (
	recordId: string,
	params: MedicalRecords.MedicalRecordSessionParams,
	options?: Omit<
		UseQueryOptions<MedicalRecords.GetMedicalRecordSessionsResponse, Error>,
		'queryKey' | 'queryFn'
	>,
) => {
	const { page, limit } = params;

	return useQuery({
		queryKey: ['medical-record-sessions', recordId, page, limit],
		queryFn: () => {
			return MedicalRecordService.getSessions(recordId, params);
		},
		enabled: !!recordId,
		refetchOnWindowFocus: false,
		...options,
	});
};

export const useGetMedicalRecordSession = (
	recordId: string,
	sessionId: string,
	options?: UseGetMedicalRecordSessionOptions,
) => {
	const { pollMode = 'post-process', ...queryOptions } = options ?? {};

	return useQuery({
		queryKey: ['medical-record-session', recordId, sessionId, pollMode],
		queryFn: () => {
			return MedicalRecordService.getSession(recordId, sessionId);
		},
		enabled: !!recordId && !!sessionId,
		refetchOnWindowFocus: false,
		refetchInterval: (query) =>
			pollMode === 'until-summary'
				? resolveMedicalRecordSessionUntilSummaryPollInterval(
						query.state.data,
				  )
				: resolveMedicalRecordSessionPollInterval(query.state.data),
		...queryOptions,
	});
};
